import React, {useContext, useEffect, useState} from "react";
import {BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid} from "recharts";
import axiosInstance from "../../AxiosInstance";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClipboard} from "@fortawesome/free-solid-svg-icons";
import Modal from "../modal/modal";
import {SelectInput, TextInput} from "../input/inputs";
import MyContext from "../../Context";

export default function ConsumptionChart({variables}) {
    const [consumptionData, setConsumptionData] = useState(null);
    const [period, setPeriod] = useState('7d');
    const [showDashboardModal, setShowDashboardModal] = useState(false);
    const [form, setForm] = useState({});
    const {dashboards} = useContext(MyContext);

    const consumptionVar = (variables || []).find(v => v.is_consumption_counter);

    useEffect(() => {
        if (consumptionVar) {
            axiosInstance.get(`variable_data/${consumptionVar.id}/consumption_histogram/?period=${period}`)
                .then(res => setConsumptionData(res.data))
                .catch(() => setConsumptionData(null));
        }
    }, [consumptionVar?.id, period]);

    if (!consumptionVar) return null;

    const samplingOptions = [
        {id: 0, name: '24 Hours'},
        {id: 1, name: '7 Days'},
        {id: 2, name: '30 Days'},
        {id: 3, name: 'Monthly'}
    ];

    const addToDashboard = () => {
        const payload = {
            name: form.widget_name,
            key: consumptionVar.id,
            type: 7,
        };
        if (form.widget_sampling !== null && form.widget_sampling !== undefined && form.widget_sampling !== '') {
            payload.widget_sampling = parseInt(form.widget_sampling);
        }
        axiosInstance.post(`dashboard/${form.dashboard_id}/add_widget/`, payload).then(() => {
            window.location.reload();
        });
    };

    const chartData = (() => {
        if (!consumptionData) return [];
        return (consumptionData.data || []).map(d => ({
            ...d,
            consumption: d.value
        }));
    })();

    const aggregationLabel = consumptionData?.aggregation_type === 'AVG' ? 'Avg' : 'Total';

    return (
        <div style={{
            width: '50%',
            backgroundColor: '#FFF',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e2e8f0',
            fontFamily: 'Poppins'
        }}>
            <Modal show={showDashboardModal} setter={setShowDashboardModal} name={'Add to Dashboard'} onClick={addToDashboard} buttonName={'Create'}>
                <div className={'mb-3'}>
                    <SelectInput name={'Dashboard'} internalName={'dashboard_id'} form={form} setForm={setForm} options={dashboards}/>
                    <SelectInput name={'Sampling'} internalName={'widget_sampling'} form={form} setForm={setForm} options={samplingOptions}/>
                    <TextInput name={'Name'} internalName={'widget_name'} form={form} setForm={setForm}/>
                </div>
            </Modal>

            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <div style={{fontSize: '15px', fontWeight: 600, color: '#334155'}}>Consumption ({aggregationLabel})</div>
                    <FontAwesomeIcon
                        role={'button'}
                        icon={faClipboard}
                        onClick={() => {
                            const device = JSON.parse(localStorage.getItem('device'));
                            const deviceName = device?.name || '';
                            const defaultName = deviceName ? `Consumption - ${deviceName}` : 'Consumption';
                            setForm({widget_name: defaultName, widget_sampling: ''});
                            setShowDashboardModal(true);
                        }}
                        color={'#94a3b8'}
                        style={{cursor: 'pointer', fontSize: '13px'}}
                    />
                </div>
                <div style={{display: 'flex', gap: '12px'}}>
                    {['24h', '7d', '30d', 'monthly', 'yearly'].map(p => (
                        <span key={p} onClick={() => setPeriod(p)} style={{
                            cursor: 'pointer',
                            fontWeight: period === p ? 700 : 400,
                            fontSize: '12px',
                            color: period === p ? '#2563EB' : '#94A3B8',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            backgroundColor: period === p ? '#EFF6FF' : 'transparent'
                        }}>
                            {p === '24h' ? '24h' : p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : p === 'monthly' ? 'Monthly' : 'Yearly'}
                        </span>
                    ))}
                </div>
            </div>
            {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={247}>
                    <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                        <XAxis dataKey="day_label" tick={{fontSize: 11}}/>
                        <YAxis tick={{fontSize: 11}}/>
                        <Tooltip/>
                        <Bar dataKey="consumption" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Consumption"/>
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#94A3B8',
                    fontSize: '13px',
                    backgroundColor: '#F8FAFC',
                    borderRadius: '8px',
                    height: '247px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    No consumption data yet. Data will appear after the hourly calculation runs.
                </div>
            )}
        </div>
    );
}
